import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { renderProfileParts } from "./parts";

/**
 * Cluster template — each section gets its own floating glass pane instead
 * of being stacked in one card. Renders as a vertical column of independent
 * panels: identity, widgets, socials/links, audio, meta.
 *
 * The "card" returned by this template is just an invisible flex container;
 * each child panel provides its own backing glass.
 */
export function renderCluster(
  config: ProfileConfig,
  options: { gateOpened: Promise<void> } = { gateOpened: Promise.resolve() },
): HTMLElement {
  const p = renderProfileParts(config, options);
  p.card.classList.add("card--cluster");
  p.card.setAttribute("data-frame", "off");

  const identityPane = pane("identity", [
    el("div", { class: "card__header" }, [
      p.avatar,
      el("div", { class: "card__identity" }, [
        el("div", { class: "card__name-row" }, [p.username, p.badges]),
        p.description,
      ]),
    ]),
  ]);

  const metaPane = (() => {
    const meta = el("div", { class: "card__meta card__meta--cluster" });
    const vc = p.meta.getInline("views");     if (vc) meta.append(vc);
    const loc = p.meta.getInline("location"); if (loc) meta.append(loc);
    const clk = p.meta.getInline("clock");    if (clk) meta.append(clk);
    const ct = p.meta.getInline("contact");   if (ct) meta.append(ct);
    return meta.childElementCount > 0 ? pane("meta", [meta]) : null;
  })();

  p.card.append(identityPane);
  if (p.widgets) p.card.append(pane("widgets", [p.widgets]));
  if (p.socials || p.links) {
    const both = el("div", { class: "cluster__action-pane" });
    if (p.socials) both.append(p.socials);
    if (p.links) both.append(p.links);
    p.card.append(pane("links", [both]));
  }
  if (metaPane) p.card.append(metaPane);
  if (p.audio) p.card.append(pane("audio", [p.audio]));
  p.meta.applyCornerZones();

  return p.card;
}

function pane(name: string, children: HTMLElement[]): HTMLElement {
  return el("section", { class: `cluster__pane cluster__pane--${name}`, "data-slot": name }, children);
}
